import React, { useCallback, useEffect, useState } from 'react'
import MainIcon from '../../../../../img/svg/main'
import { ButtonsWrapper, ProcessPaymentTap2PayContent, ProcessPaymentText } from '../index.styled'
import HourglassLoader from '../../../loaders/HourglassLoader'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getAccountDetailsThunk, getTap2PayConnectionTokenThunk } from '../../../../../redux/thunk/cartPayment/tap2PayPayment'
import { publishTap2PayConnectionTokenCreatedMessage } from '../../../../../helper/tap2PayMessages'
import Button from '../../../buttons/Button'
import CustomModal from '../../../modal/CustomModal'
import { setCartPaymentTap2PayAccountDetails, setCartPaymentTap2PayAccountDetailsError, setCartPaymentTap2PayConnectionToken, setCartPaymentTap2PayConnectionTokenError, setCartPaymentTap2PayEnabled, setCartPaymentTap2PayErrorType } from '../../../../../redux/action/cartPayment'
import { selectCompanyIndex } from '../../../../../redux/selector/system'
import { isDevEnvironment } from '../../../../../helper'
import { logger } from '../../../../../logger/logger'

const EVENT_Z_INDEX = 101
const ModalTap2PayAvailabilityChecker = React.memo(({ isOpen, setShowTap2PayAvailabilityCheckerModal, setShowChangePaymentAmountModal }) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const hasListenerReady = useSelector(state => state.system.nativeAppListenerReady)
	const waiterAccessToken = useSelector(state => state.modalLogin.accessToken)
	const hasModalLogin = !!waiterAccessToken
	const tap2PayConnectionToken = useSelector(state => state.cartPayment.tap2PayConnectionToken)
	const isErrorConnectionToken = useSelector(state => state.cartPayment.tap2PayConnectionTokenError)
	const accountDetails = useSelector(state => state.cartPayment.tap2PayAccountDetails)
	const accountDetailsError = useSelector(state => state.cartPayment.tap2PayAccountDetailsError)
	const isEnableTap2Pay = useSelector(state => state.cartPayment.tap2PayEnabled)
	const errorType = useSelector(state => state.cartPayment.tap2PayErrorType)
	const [createConnectionTokenIsLoading, setCreateConnectionTokenIsLoading] = useState(false)
	const disabledCloseButton = createConnectionTokenIsLoading || (tap2PayConnectionToken && isEnableTap2Pay === null)
	const companyIndex = useSelector(selectCompanyIndex)
	const displayError = isErrorConnectionToken || isEnableTap2Pay === false || accountDetailsError
	const messageError = isErrorConnectionToken ? t('Tap2PayModals.availableCheck.errorConnectionToken') : t('Tap2PayModals.availableCheck.error')
	const isIosVersionError = errorType === 'IOS_VERSION_UNSUPPORTED'
	const isGeoPermissionError = errorType === 'GEOLOCATION_PERMISSION_DENIED'
	const isDev = isDevEnvironment()

	useEffect(() => {
		if (isOpen && !tap2PayConnectionToken) {
			fetchConnectionToken()
			dispatch(getAccountDetailsThunk())
		}
	}, [isOpen, tap2PayConnectionToken])// eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isOpen && hasListenerReady && tap2PayConnectionToken && accountDetails) {
			publishConnectionToken()
		}
	}, [tap2PayConnectionToken, isOpen, hasListenerReady, accountDetails]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isOpen && isEnableTap2Pay) {
			setShowTap2PayAvailabilityCheckerModal(false)
			setShowChangePaymentAmountModal(true)
		}
	}, [isEnableTap2Pay, isOpen]) //eslint-disable-line react-hooks/exhaustive-deps

	const fetchConnectionToken = useCallback(async () => {
		setCreateConnectionTokenIsLoading(true)
		await dispatch(getTap2PayConnectionTokenThunk(hasModalLogin))
		setCreateConnectionTokenIsLoading(false)
	}, [hasModalLogin, dispatch])

	const publishConnectionToken = useCallback(() => {
		logger.info(`Publish connection token to native app ${companyIndex}. Connection token:${tap2PayConnectionToken} AccountDetails: ${accountDetails} `)
		publishTap2PayConnectionTokenCreatedMessage(tap2PayConnectionToken, isDev, accountDetails)
	}, [tap2PayConnectionToken, companyIndex, isDev, accountDetails])

	const handleClose = useCallback(() => {
		if (disabledCloseButton) return
		dispatch(setCartPaymentTap2PayConnectionToken(null))
		setShowTap2PayAvailabilityCheckerModal(false)
		dispatch(setCartPaymentTap2PayEnabled(null))
		dispatch(setCartPaymentTap2PayErrorType(null))
		dispatch(setCartPaymentTap2PayConnectionTokenError(null))
		dispatch(setCartPaymentTap2PayAccountDetails(null))
		dispatch(setCartPaymentTap2PayAccountDetailsError(null))
	}, [dispatch, disabledCloseButton, setShowTap2PayAvailabilityCheckerModal])

	const handleRetryGetConnectionToken = useCallback(() => {
		fetchConnectionToken()
		dispatch(getAccountDetailsThunk())
	}, [dispatch, fetchConnectionToken])

	return (
		<CustomModal isOpen={isOpen}
								 close={handleClose}
								 id="modal-tap2pay-availability-checker"
								 size="x-small"
								 title={t('Tap2PayModals.payment')}
								 disableBackButton={true}
								 zIndex={EVENT_Z_INDEX}
								 showCloseButton={!disabledCloseButton}
								 enableOutsideClick={!disabledCloseButton}
								 button={displayError && <ButtonsWrapper>
									 <Button name="cancel" icon="clear" text={t('buttons.controls.cancel')} zIndex={EVENT_Z_INDEX} color="gray" onClick={handleClose} disabled={disabledCloseButton} />
									 {isErrorConnectionToken && <Button name="retry" icon="retry" text={t('Tap2PayModals.retry')} zIndex={EVENT_Z_INDEX} color="blue" onClick={handleRetryGetConnectionToken} disabled={disabledCloseButton} />}
								 </ButtonsWrapper>}>
			<ProcessPaymentTap2PayContent>
				{displayError && (
					<>
						{isIosVersionError &&
							<>
								<MainIcon icon="sad" />
								<ProcessPaymentText className="gray">{t('Tap2PayModals.availableCheck.errorIOSVersion')}</ProcessPaymentText>
							</>
						}
						{isGeoPermissionError &&
							<>
								<MainIcon icon="switches" />
								<ProcessPaymentText className="gray">{t('Tap2PayModals.availableCheck.errorGeoPermission')}</ProcessPaymentText>
							</>
						}
						{!errorType &&
							<>
								<MainIcon icon="error" />
								<ProcessPaymentText className="error">{messageError}</ProcessPaymentText>
							</>
						}
					</>
				)}
				{!displayError && <>
					<HourglassLoader className="dark" />
					<ProcessPaymentText>{t('Tap2PayModals.availableCheck.text')}</ProcessPaymentText>
				</>}
			</ProcessPaymentTap2PayContent>
		</CustomModal>
	)

})
ModalTap2PayAvailabilityChecker.displayName = 'ModalTap2PayAvailabilityChecker'
export default ModalTap2PayAvailabilityChecker
